import LocalData from './localData.js';
import products from './products.js';
import popup from './popup.js';
import events from './event.js';
import Storage from './storage.js';
import Logger from './logger.js';
import { textString, buttonString } from './texts.js';
import FrameZoomView from './FrameZoomView.js';
import { getGraphicKind } from './textStep.js';
import _ from './translate.js';

class ProductPage {
    onPageWrapper = null;
    cropperPreviewDiv = null;
    cropper = null;
    data = null;
    originalCartItemKey = null; // Store cart item key for edit tracking (session-specific)
    addToCartClicked = false;
    reuploadPopupQueued = false;

    constructor() {
        this.init();
        this.helpBackHistory();
    }

    async init() {
        await this.initSessionData();

        // Initialize the product page if on selected product page
        const selectProduct = Storage.get('selectProduct');
        Logger.debug('ProductPage.init - selectProduct from Storage:', selectProduct);

        if (!selectProduct) {
            Logger.debug('No product selected, skipping product page initialization');
            return;
        }
        const theProducts = await products.getProducts();
        Logger.debug('ProductPage.init - theProducts:', theProducts);

        const product_permalink = theProducts.find(p => p.value == selectProduct)?.permalink;
        Logger.debug('ProductPage.init - product_permalink for selectProduct:', product_permalink);

        // check if window location base url is same as product_permalink
        const currentBaseUrl = window.location.origin + window.location.pathname;
        Logger.debug('ProductPage.init - currentBaseUrl:', currentBaseUrl);
        Logger.debug('ProductPage.init - Match?', product_permalink && currentBaseUrl == product_permalink);

        // In edit mode, always use the current product page URL as the authoritative source
        // This handles cases where the stored selectProduct doesn't match the actual product page
        if (this.originalCartItemKey) {
            const currentProduct = theProducts.find(p => p.permalink == currentBaseUrl);
            if (currentProduct) {
                if (selectProduct != currentProduct.value) {
                    Logger.log('Edit mode: Updating selectProduct from', selectProduct, 'to', currentProduct.value);
                    Storage.save('selectProduct', currentProduct.value);
                }
                this.initProductPage();
            } else {
                Logger.debug('Edit mode: Could not find product matching current URL:', currentBaseUrl);
            }
        } else if (product_permalink && currentBaseUrl == product_permalink) {
            // Normal mode: URL must match stored selectProduct
            this.initProductPage();
        } else {
            Logger.debug('Product page URL does not match selectProduct permalink, skipping initialization');
        }
    }

    helpBackHistory() {
        window.onpageshow = (event) => {
            if (event.persisted) {
                popup.destroy();
                // Visa tumnageln igen vid bfcache-restore
                this.addThumbnail();
            }
        };
        // Visa tumnageln igen när fliken återaktiveras (kan tappas vid bakgrundsflik)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.addThumbnail();
            }
        });
    }

    async initSessionData() {
        // Only initialize session data if we're on a product page
        if (!document.body.classList.contains('mscp-product')) return;

        const urlParams = new URLSearchParams(window.location.search);

        // Check for edit mode - store cart item key in class variable
        const cartItemKey = urlParams.get('mscp_edit_key');
        const isEdit = !!cartItemKey;

        // Store cart item key for this session (if editing from cart)
        if (cartItemKey) {
            this.originalCartItemKey = cartItemKey;
        }

        if (isEdit && cartItemKey) {
            // EDIT MODE: Fetch session data from cart API using the cart item key, to ensure we have the latest data for the item being edited
            let apiSuccess = false;
            try {
                const response = await fetch(`/wp-json/mscp/v1/cart/${cartItemKey}`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data) {
                        Storage.createSessionFromData(result.data);
                        apiSuccess = true;
                        Logger.log('Edit session loaded from cart API');
                    }
                }
            } catch (e) {
                Logger.warn('Could not fetch cart item data from API:', e);
            }

            // Fallback: try with localStorage
            if (!apiSuccess) {
                const sessionID = urlParams.get('mscp_session_id');
                if (sessionID) {
                    Logger.log('API fallback: using localStorage cloneSession');
                    await Storage.cloneSession(sessionID, isEdit, null);
                } else {
                    Storage.startSession();
                    if (!Storage.get('activeImageID')) {
                        this.queueReuploadPopup();
                    }
                }
            }
        }
        else if (urlParams.has('mscp_session_id')) {
            const sessionID = urlParams.get('mscp_session_id');

            // Clone session - files will be cloned later when saving (uploadAndSaveThumbnail)
            await Storage.cloneSession(sessionID, isEdit, null);
        }
        else {
            Storage.startSession();
            // If no active image ID after starting session, it means the user came to the product page without going through the upload flow - show the upload popup, or corrupt session data
            if (!Storage.get('activeImageID')) {
                this.queueReuploadPopup();
            }
        }
    }

    // open the upload popup as soon as possible
    queueReuploadPopup() {
        if (this.reuploadPopupQueued) return;

        const productWrapper = document.querySelector('.mindstore-customizable-product-preview-wrapper') ||
            document.querySelector('.elementor-widget-mindstore_customizable_product');
        if (!productWrapper) return;

        this.reuploadPopupQueued = true;
        const openPopup = () => {
            setTimeout(() => events.trigger('openPopup'), 0);
        };

        if (document.readyState === 'complete') {
            openPopup();
        } else {
            window.addEventListener('load', openPopup, { once: true });
        }
    }

    async initProductPage() {

        this.data = LocalData.get();

        if (!this.data.activeImageID || !this.data.cropBox || !this.data.orientation || !this.data.presentation || !this.data.paper || !this.data.size || !this.data.product || !this.data.retouch) {
            Logger.warn('Missing required information for the product.');
        }

        // Show edit banner if we're editing from cart
        if (this.originalCartItemKey) {
            this.showEditBanner();
        }

        await this.setSelectedSize(); // Set the selected size based on local data and hide the size wrapper
        this.renderPreviewImage(); // Render the preview image based on the selected options, no cropper is needed here
        this.addMscpMetaDataToForm(); // Add custom meta data on form submit
        this.trackAddToCart();
        this.initInternalLinksGuard();

        events.add('imageLoaded', (image) => {
            document.querySelector('.elementor-widget-mindstore_customizable_product').style.height = this.cropperPreviewDiv.offsetHeight + 60 + 'px';
        });

    }

    showEditBanner() {
        // Remove existing banner
        const existingBanner = document.querySelector('.mscp-edit-mode-banner');
        if (existingBanner) {
            existingBanner.remove();
        }

        // Only show banner if editing from cart (originalCartItemKey exists)
        if (this.originalCartItemKey) {
            // Create banner element
            const banner = document.createElement('div');
            banner.classList.add('mscp-edit-mode-banner');
            const bannerText = textString('product_page_edit_mode_banner');
            if (!bannerText) {
                Logger.debug('Edit mode banner text not found in texts');
                return;
            }
            banner.innerHTML = bannerText;

            Logger.log('Product page: Edit mode from cart', { originalCartItemKey: this.originalCartItemKey });

            // Insert banner above .single_variation_wrap
            const variationWrap = document.querySelector('.single_variation_wrap');
            if (variationWrap) {
                variationWrap.parentNode.insertBefore(banner, variationWrap);
            } else {
                // Fallback to original location
                const productWrapper = document.querySelector('.elementor-widget-mindstore_customizable_product') ||
                    document.querySelector('.mindstore-customizable-product-preview-wrapper')?.parentElement;

                if (productWrapper) {
                    productWrapper.insertBefore(banner, productWrapper.firstChild);
                }
            }
        }
    }

    async setSelectedSize() {
        // Set the selected size based on local data
        const selectedSize = this.data.size;
        if (!selectedSize) {
            return;
        }

        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
        let applied = false;
        let sizeWrapper = null;

        for (let attempt = 0; attempt < 25; attempt++) {
            sizeWrapper = document.getElementsByClassName('pre-pa_size-wrapper')[0];

            if (sizeWrapper) {
                const sizeElement = sizeWrapper.querySelector(`span[data-value='${selectedSize}']`);
                if (sizeElement && !sizeElement.classList.contains('hidden')) {
                    this.simulateSizeOptionClick(sizeElement);
                    await sleep(60);

                    const formSelectedSizeAfterClick = this.getSelectedSizeFromForm();
                    if (formSelectedSizeAfterClick === selectedSize) {
                        applied = true;
                        break;
                    }
                }
            }

            const formSelectedSize = this.getSelectedSizeFromForm();
            if (formSelectedSize === selectedSize) {
                applied = true;
                break;
            }

            await sleep(120);
        }

        if (!applied) {
            const appliedViaNative = this.setNativeSizeField(selectedSize);
            if (appliedViaNative) {
                await sleep(100);
                applied = this.getSelectedSizeFromForm() === selectedSize;
            }
        }

        if (!applied) {
            Logger.error("Could not apply selected size %s on product page", selectedSize);
            return;
        }

        Storage.save('size', selectedSize);

        if (sizeWrapper) {
            sizeWrapper.classList.add('disabled'); // hide the size wrapper
            // sizeWrapper.addEventListener('click', () => {
            //     events.trigger('openPopup');
            // });
        }
    }

    getSelectedSizeFromForm() {
        const nativeSelect = document.querySelector('form.cart select[name="attribute_pa_size"]');
        if (nativeSelect?.value) {
            return nativeSelect.value;
        }

        const checkedRadio = document.querySelector('form.cart input[name="attribute_pa_size"]:checked');
        if (checkedRadio?.value) {
            return checkedRadio.value;
        }

        const selectedPreOption = document.querySelector('.pre-pa_size-wrapper .selected[data-value]');
        if (selectedPreOption?.dataset?.value) {
            return selectedPreOption.dataset.value;
        }

        return '';
    }

    setNativeSizeField(size) {
        let updated = false;

        const nativeSelect = document.querySelector('form.cart select[name="attribute_pa_size"]');
        if (nativeSelect) {
            nativeSelect.value = size;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            updated = true;
        }

        const radios = document.querySelectorAll('form.cart input[name="attribute_pa_size"]');
        if (radios.length > 0) {
            radios.forEach((radio) => {
                radio.checked = (radio.value === size);
            });
            const checked = document.querySelector('form.cart input[name="attribute_pa_size"]:checked');
            checked?.dispatchEvent(new Event('change', { bubbles: true }));
            updated = true;
        }

        return updated;
    }

    simulateSizeOptionClick(element) {
        const eventsToDispatch = [
            'pointerdown',
            'mousedown',
            'pointerup',
            'mouseup',
            'click'
        ];

        eventsToDispatch.forEach((eventName) => {
            element.dispatchEvent(new MouseEvent(eventName, {
                bubbles: true,
                cancelable: true,
                view: window
            }));
        });

        element.click();
    }

    renderPreviewImage() {
        this.onPageWrapper = document.querySelector('.mindstore-customizable-product-preview-wrapper');
        if (!this.onPageWrapper) {
            Logger.error('Preview wrapper not found on the product page step');
            return;
        }
        this.onPageWrapper.innerHTML = ''; // Clear existing content

        // Create a wrapper for the cropper preview
        const previewWrapper = document.createElement('div');
        previewWrapper.classList.add('mscp-crop-wrapper');
        previewWrapper.classList.add('on-product-page');
        this.onPageWrapper.appendChild(previewWrapper);

        // Create the cropper preview div
        this.cropperPreviewDiv = document.createElement('div');
        this.cropperPreviewDiv.classList.add('mscp-crop-preview');
        previewWrapper.appendChild(this.cropperPreviewDiv);

        this.addThumbnail();
        this.addOpenPopupButton(document.querySelector('.mindstore-customizable-product-preview-wrapper'));
        this.addEnlargeButton(this.cropperPreviewDiv);
    }

    /**
     * "Förstora"-knapp bredvid pennan på artikelkortet (spec entry 2). Öppnar
     * en gallery-liknande fullskärmsvy med pan/zoom av den inramade tavlan.
     *
     * Produktsidan visar bara en tillplattad miniatyr, så vi bygger om stacken
     * från sessionen och laddar FULL-RES-fotot i zoom-vyn — annars skulle en
     * inzoomning bli suddig. Miniatyren används endast som reserv.
     */
    addEnlargeButton(element) {
        if (!element) return;
        const btn = document.createElement('span');
        btn.classList.add('mscp-enlarge-button', 'mscp-floating-button');
        btn.title = buttonString('enlarge_button', _('Enlarge'));
        btn.innerHTML = (window.mindstore_object?.icon?.search) || '⤢';
        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const photoUrl = await this.resolveFullResPhotoUrl();
            FrameZoomView.open({ source: 'session', photoUrl });
        });
        element.appendChild(btn);
    }

    /**
     * Hämtar full-res-fotot via check_image (samma väg som customizern använder
     * för att återställa bilden). Faller tillbaka på miniatyren om servern inte
     * har någon kopia.
     */
    async resolveFullResPhotoUrl() {
        const id = Storage.get('activeImageID');
        if (id) {
            try {
                const body = new FormData();
                body.append('id', id);
                const res = await fetch('/wp-json/mscp/v1/check_image', { method: 'POST', body, credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    if (data?.copy_url) return data.copy_url;
                }
            } catch (err) {
                Logger.debug('resolveFullResPhotoUrl: check_image failed, using thumbnail', err);
            }
        }
        return Storage.get('thumbnailUrl') || null;
    }

    addThumbnail() {
        try {

            const thumbnailUrl = Storage.get('thumbnailUrl');
            if (thumbnailUrl) {
                let preview = document.getElementById('product_thumb_preview')
                let img = preview?.querySelector('img');
                if (!preview) {
                    preview = document.createElement('div');
                    preview.id = 'product_thumb_preview';
                    this.cropperPreviewDiv.appendChild(preview);

                    // dispatch event msap_preview_image_ready when image is loaded
                    const previewReadyEvent = new CustomEvent('msap_preview_image_ready', {
                        detail: {
                            preview: preview // Skickar med referensen till elementet
                        }
                    });
                    document.dispatchEvent(previewReadyEvent);

                }
                preview.classList.add('loading');
                // Graphic looks: the thumbnail is the finished artwork (paper and text included), shown whole
                // and flat; the frame/passepartout overlays assume a plain photo and would crop the text.
                const gfx = !!getGraphicKind();
                preview.classList.toggle('mscp-gfx-preview', gfx);
                this.cropperPreviewDiv.classList.toggle('mscp-gfx-preview-host', gfx);
                if (!img) {
                    img = new Image();
                    preview?.appendChild(img);
                    img.decoding = 'async';
                    img.loading = 'eager';
                }

                img.addEventListener('load', () => {
                    preview.classList.remove('loading');
                }, { once: true });

                img.addEventListener('error', (e) => {
                    preview?.remove();
                    Logger.error('Could not load thumbnail', e);
                }, { once: true });

                img.src = thumbnailUrl;

            }
        }
        catch (e) {
            console.error(e);
        }
    }

    addOpenPopupButton(element, deviceType = null) {
        if (!element) return;
        const openPopupButton = document.createElement('div');
        openPopupButton.classList.add('mscp-open-popup-button', 'mscp-button');
        if (deviceType) {
            openPopupButton.classList.add(deviceType);
        }

        const openPopupLabel = document.createElement('span');
        openPopupLabel.classList.add('mscp-button-label');
        openPopupButton.appendChild(openPopupLabel);

        openPopupLabel.innerHTML = buttonString('product_page_open_popup_button', _('Upload'));
        element.appendChild(openPopupButton);
        openPopupButton.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent the click event from bubbling up
            e.preventDefault(); // Prevent default action if any
            // events.trigger('openEditPopup');
            events.trigger('openPopup');
        });
    }

    addMscpMetaDataToForm() {
        const form = document.querySelector('form.cart');
        if (!form) {
            Logger.error('Cart not found');
            return;
        }

        //add hidden input with custom data to the form
        const existingInput = form.querySelector('input[name="mscp_meta_data"]');
        if (existingInput) {
            existingInput.remove(); // Remove existing input if it exists
        }
        const metaData = LocalData.get();
        if (!metaData) {
            Logger.error('No meta data found in LocalData');
            return;
        }

        const refreshMetaDataInput = () => {
            const latestMetaData = LocalData.get() || {};
            const formSize = this.getSelectedSizeFromForm();
            if (formSize) {
                latestMetaData.size = formSize;
                Storage.save('size', formSize);
            }
            input.value = JSON.stringify(latestMetaData);
        };

        // Create hidden input
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'mscp_meta_data';
        input.value = JSON.stringify(metaData); // Convert meta data to JSON string
        form.appendChild(input); // Append the input to the form
        Logger.debug("Meta data added to form:", metaData);

        form.addEventListener('submit', () => {
            refreshMetaDataInput();
        });

        const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"], .single_add_to_cart_button, .add_to_cart_button');
        submitButtons.forEach((button) => {
            button.addEventListener('click', () => {
                refreshMetaDataInput();
            });
        });

        // also set onPageWrapper data attributes
        if (this.onPageWrapper) {
            if (metaData?.presentation) {
                this.onPageWrapper.setAttribute('data-presentation', metaData.presentation);
            }
            if (metaData?.paper) {
                this.onPageWrapper.setAttribute('data-paper', metaData.paper);
            }
        }
    }

    trackAddToCart() {
        const form = document.querySelector('form.cart');
        if (!form) {
            return;
        }

        form.addEventListener('submit', () => {
            this.addToCartClicked = true;
        });

        const addToCartButton = form.querySelector('button[type="submit"], input[type="submit"]');
        if (addToCartButton) {
            addToCartButton.addEventListener('click', () => {
                this.addToCartClicked = true;
            });
        }

        const ajaxButtons = form.querySelectorAll('.single_add_to_cart_button, .add_to_cart_button');
        ajaxButtons.forEach((button) => {
            button.addEventListener('click', () => {
                this.addToCartClicked = true;
            });
        });

        const jq = window.jQuery;
        if (typeof jq === 'function') {
            jq(document.body).on('adding_to_cart added_to_cart', () => {
                this.addToCartClicked = true;
            });
        }
    }

    initInternalLinksGuard() {
        document.addEventListener('click', (e) => {
            const anchor = e.target.closest('a');
            if (!anchor || !anchor.href || anchor.target === '_blank') return;

            // Kontrollera om det är en intern länk
            const url = new URL(anchor.href, window.location.origin);
            const isInternal = url.origin === window.location.origin;
            const isSamePage = url.pathname === window.location.pathname && url.search === window.location.search;

            if (isInternal && !isSamePage && this.shouldWarn(anchor)) {
                e.preventDefault();
                this.showNavWarning(anchor.href);
            }
        });
    }

    shouldWarn(anchor) {
        if (this.addToCartClicked) return false;
        // ignore if click is in popup
        if (anchor.closest('.mscp-popup-overlay')) return false;
        // ignore has some parent with #moutife-mini-cart-side
        if (anchor.closest('#moutife-mini-cart-side')) return false;

        return Boolean(LocalData.get() || (typeof Storage.getCurrentSessionId === 'function' && Storage.getCurrentSessionId()));
    }

    showNavWarning(targetUrl) {
        const overlay = document.createElement('div');
        overlay.classList.add('mscp-popup-overlay', 'active', 'mscp-nav-guard-overlay');

        const modal = document.createElement('div');
        modal.classList.add('mscp-popup-content', 'mscp-nav-guard-modal');

        const title = textString('product_page_nav_warning_title') || 'Unsaved changes';
        const body = textString('product_page_nav_warning_body') || '<p>You have unsaved changes. Are you sure you want to leave this page?</p>';
        const stayText = textString('product_page_nav_warning_stay') || 'Stay on page';
        const leaveText = textString('product_page_nav_warning_leave') || 'Leave page';

        modal.innerHTML = `
            <div class="mscp-text-wrapper">
                <h3>${title}</h3>
                <div class="mscp-nav-guard-body">${body}</div>
                <div class="mscp-nav-guard-buttons">
                    <span class="mscp-button mscp-button-secondary stay-btn">${stayText}</span>
                    <span class="mscp-button leave-btn">${leaveText}</span>
                </div>
            </div>
        `;

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        modal.querySelector('.stay-btn').onclick = () => overlay.remove();
        modal.querySelector('.leave-btn').onclick = () => {
            this.addToCartClicked = true; // Förhindra dubbel varning
            window.location.href = targetUrl;
        };
    }

}
const productPage = new ProductPage();
export default productPage;
